import SubframeCore, { IconName } from "@subframe/core";
import { Recipient } from "../redux/interfaces/reducersInterfaces";
import { Message } from "../Interfaces/messagesInterface";
import moment from "moment";
import { getCountryCodeFromNumber } from "../Utils/countryNameToCode";
import { formatPhoneNumber } from "../Utils/formatPhoneNumber";

interface RecipientProps {
  recipient: Recipient;
  lastMessage: Message;
  /** Rendered beside the time (e.g. FeatherMoreVertical for Mark as unread) - keeps a fixed place */
  rightActions?: React.ReactNode;
}

function isConversationUnread(lastMessage: Message | null | undefined) {
  return (
    String(lastMessage?.status ?? "").trim().toLowerCase() === "unread"
  );
}

function Friend({ recipient, lastMessage, rightActions }: RecipientProps) {
  const countryCode = recipient && getCountryCodeFromNumber(recipient?.phone_number.toString());
  
  // Safety check - if recipient is invalid, don't render
  if (!recipient || !recipient.id || !recipient.phone_number) {
    return null;
  }
  
  // Safety check - ensure lastMessage is valid
  if (!lastMessage || typeof lastMessage !== 'object') {
    return null;
  }

  const unread = isConversationUnread(lastMessage);

  return (
    <>
      <svg
        className="h-12 w-12 flex-none rounded-full"
        xmlns="http://www.w3.org/2000/svg"
        width="401px"
        height="401px"
        enable-background="new 312.809 0 401 401"
        version="1.1"
        viewBox="312.809 0 401 401"
      >
        <g transform="matrix(1.223 0 0 1.223 -467.5 -843.44)">
          <rect x="601.45" y="653.07" width="401" height="401" fill="#E4E6E7" />
          <path
            d="m802.38 908.08c-84.515 0-153.52 48.185-157.38 108.62h314.79c-3.87-60.44-72.9-108.62-157.41-108.62z"
            fill="#AEB4B7"
          />
          <path
            d="m881.37 818.86c0 46.746-35.106 84.641-78.41 84.641s-78.41-37.895-78.41-84.641 35.106-84.641 78.41-84.641c43.31 0 78.41 37.9 78.41 84.64z"
            fill="#AEB4B7"
          />
        </g>
      </svg>
      <div className="flex min-w-0 grow shrink-0 basis-0 flex-col items-start cursor-pointer select-none">
        <div className="flex w-full min-w-0 items-center gap-2">
          <span
            className={`conveythis-no-translate min-w-0 truncate text-body-bold font-body-bold text-default-font ${
              unread ? "font-extrabold" : ""
            }`}
          >
            {recipient.name
              ? recipient.name
              : recipient.phone_number
              ? formatPhoneNumber(
                  recipient.phone_number,
                  countryCode?.countryCode ? countryCode.countryCode : "US"
                )
              : "No name available"}
          </span>
          <div className="flex shrink-0 grow basis-0 items-center justify-end gap-2 self-stretch">
            <span
              className={`text-caption font-caption text-default-font ${
                unread ? "font-bold text-success-500" : ""
              }`}
            >
              {lastMessage?.created_at
                ? moment(lastMessage.created_at).isSame(moment(), "day")
                  ? moment(lastMessage.created_at).local().format("hh:mm A")
                  : moment(lastMessage.created_at).local().format("D/MM/YYYY")
                : ""}
            </span>
            {unread && (
              <SubframeCore.Icon
                className="text-caption-bold font-caption-bold text-success-500"
                name="FeatherMail"
              />
            )}
          </div>
          {rightActions}
        </div>
        <div className="flex w-full flex-col items-start gap-1">
          <SubframeCore.Icon
            className="hidden text-body-bold font-body-bold text-subtext-color"
            name="FeatherCornerUpLeft"
          />
          <div className="flex w-full items-center justify-end gap-1">
            <SubframeCore.Icon
              className={`text-body font-body ${
                lastMessage?.status === "read"
                  ? "text-brand-500"
                  : "text-subtext-color"
              } ${lastMessage?.recipient && "hidden"}`}
              name={
                (lastMessage?.status === "delivered" ||
                lastMessage?.status === "read"
                  ? "FeatherCheckCheck"
                  : "FeatherCheck") as IconName
              }
            />
            <span
              className={`conveythis-no-translate line-clamp-1 grow shrink-0 basis-0 text-body font-body text-default-font ${
                unread ? "font-bold" : ""
              }`}
            >
              {lastMessage &&
                lastMessage.system_user &&
                typeof lastMessage.system_user === 'string' &&
                lastMessage.system_user}
              {lastMessage?.recipient && typeof lastMessage.recipient === 'string' && lastMessage.recipient}
            </span>
          </div>
          <div className="conveythis-no-translate flex flex-wrap items-start gap-1">
            {Array.isArray(recipient.global_label) &&
              recipient.global_label.map((label: string, index: number) => {
                const isHuman = label?.toLowerCase() === "human";
                return (
                  <div
                    key={`ai-${index}`}
                    className={`flex items-center justify-center rounded-md border border-solid px-1 py-px ${
                      isHuman
                        ? "border-success-400 bg-success-300"
                        : "border-warning-300 bg-warning-200"
                    }`}
                  >
                    <span
                      className={`grow shrink-0 basis-0 self-stretch text-caption font-caption text-center ${
                        isHuman ? "text-success-900" : "text-warning-900"
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                );
              })}

            {Array.isArray(recipient.manual_label) &&
              recipient.manual_label.map((label: string, index: number) => (
                <div
                  key={`manual-${index}`}
                  className="flex items-center justify-center rounded-md border border-solid border-success-400 bg-success-300 px-1 py-px"
                >
                  <span className="grow shrink-0 basis-0 self-stretch text-caption font-caption text-success-900 text-center">
                    {label}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default Friend;
